import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Card } from "@/components/portal/ui";
import { formatIn } from "@/lib/classTime";

const inp = "border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-[#9F634E]/50";
const err = (e, f) => { const d = e?.response?.data?.detail; toast.error(Array.isArray(d) ? d[0]?.msg : d || f); };

function Family({ f, classes, onDone }) {
  const opts = classes.filter((k) => k.course === f.course && k.seats_left > 0);
  const [cid, setCid] = useState("");
  const place = async () => {
    try { await http.post(`/admin/academics/faculty-pending/${f.customer_id}/${f.course}/assign`, { cohort_id: cid }); toast.success("Meetings confirmed — the family has been emailed"); onDone(); }
    catch (e) { err(e, "Could not confirm"); }
  };
  return (
    <div className="py-3 border-t border-[#F0ECE4] flex flex-wrap gap-3 justify-between items-center" data-testid={`pending-family-${f.course}-${f.customer_id}`}>
      <div className="text-sm"><div className="font-medium text-[#1A1918]">{f.name || f.email} · {f.code}</div>
        <div className="text-xs text-[#8A998C]"><a href={`mailto:${f.email}`} className="underline">{f.email}</a>{f.phone ? ` · ${f.phone}` : ""} · enrolled {f.enrolled_at ? new Date(f.enrolled_at).toLocaleDateString() : "—"} · {f.via === "stripe" ? "paid via Stripe" : f.via}</div></div>
      <div className="flex gap-2 items-center">
        <select value={cid} onChange={(e) => setCid(e.target.value)} className={inp} data-testid={`pending-class-${f.course}-${f.customer_id}`}>
          <option value="">{opts.length ? "Choose the class with their confirmed dates…" : "Create a class for this course below first"}</option>
          {opts.map((k) => <option key={k.id} value={k.id}>{k.name} · starts {formatIn(k.first, k.timezone)} · {k.seats_left} seats left{k.has_link ? "" : " · no link yet"}</option>)}
        </select>
        <button onClick={place} disabled={!cid} data-testid={`pending-confirm-${f.course}-${f.customer_id}`} className="bg-[#1A1918] text-white rounded-full px-4 py-2 text-sm disabled:opacity-40">Confirm meetings</button>
      </div>
    </div>
  );
}

// Direct Faculty-Led enrollment: families pay, then Andrea arranges the four meetings personally (e.g. Calendly) and confirms them here.
export function FacultyAvailability() {
  const [d, setD] = useState(null);
  const [cap, setCap] = useState(12);
  const [cal, setCal] = useState("");
  const load = () => http.get("/admin/academics/faculty-pending").then((r) => { setD(r.data); setCap(r.data.status.capacity); setCal(r.data.status.calendly_url || ""); }).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const s = d.status;
  const save = async (open) => {
    try { await http.put("/admin/academics/faculty-enrollment", { direct_open: open, max_unscheduled: Number(cap), calendly_url: cal }); toast.success("Saved"); load(); }
    catch (e) { err(e, "Could not save"); }
  };
  return (
    <Card className="p-5 mb-6" data-testid="faculty-enrollment">
      <div className="flex flex-wrap justify-between gap-3 items-start">
        <div>
          <div className="font-medium text-[#1A1918]">Faculty-Led enrollment · families awaiting scheduling <span className="ml-2 text-xs rounded-full px-2.5 py-1 bg-[#F3EFE9] text-[#9F634E]" data-testid="pending-count">{d.families.length}</span></div>
          <p className="text-xs text-[#6B665E] mt-1 max-w-2xl">Families enroll and pay without choosing dates. Contact each family (for example through Calendly), create a class below with the four confirmed dates, time zone and meeting link, then confirm them here. Reminders start automatically. Classes can't overlap your other meetings, and the maximum is six families per class.</p>
        </div>
        <div className="text-sm space-y-2" data-testid="faculty-enrollment-settings">
          <div data-testid="faculty-enrollment-state">{s.enabled ? (s.open ? "Open for enrollment" : "Paused: awaiting-scheduling limit reached") : "Closed by you"} · {s.awaiting} of {s.capacity} places in use</div>
          <div className="flex gap-2 items-center">
            <label className="text-xs">Max families awaiting scheduling <input type="number" min={0} max={200} value={cap} onChange={(e) => setCap(e.target.value)} className={`${inp} w-20 ml-1`} data-testid="faculty-enrollment-capacity" /></label>
            <button onClick={() => save(s.enabled)} data-testid="faculty-enrollment-save" className="border border-[#1A1918] rounded-full px-3 py-1.5 text-xs">Save</button>
            <button onClick={() => save(!s.enabled)} data-testid="faculty-enrollment-toggle" className="border border-[#1A1918] rounded-full px-3 py-1.5 text-xs">{s.enabled ? "Close enrollment" : "Open enrollment"}</button>
          </div>
          <label className="text-xs block">Your Calendly (scheduling) link, shown to families awaiting scheduling
            <input value={cal} onChange={(e) => setCal(e.target.value)} placeholder="https://calendly.com/…" className={`${inp} w-full mt-1`} data-testid="faculty-calendly-url" /></label>
        </div>
      </div>
      {d.families.length === 0 ? <p className="text-sm text-[#8A998C] mt-3">No families are waiting to be scheduled.</p>
        : d.families.map((f) => <Family key={`${f.course}-${f.customer_id}`} f={f} classes={d.classes} onDone={load} />)}
    </Card>
  );
}
